/**
 * Liste des comptes (docs/16 §2).
 *
 * Les filtres vivent dans l'URL plutôt que dans un état local : une recherche
 * partagée ou rechargée doit rendre la même page, et un administrateur qui
 * revient de la fiche retrouve sa liste telle qu'il l'avait laissée.
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useAdminRoles, useAdminUsers } from "@/features/admin/api/useAdmin";

import { StatusBadge } from "@/features/admin/components/StatusBadge";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { LoadMore } from "@/shared/ui/LoadMore";
import { Skeleton } from "@/shared/ui/Skeleton";

const STATUSES = ["pending", "active", "suspended", "deactivated"] as const;

export function AdminUsersPage() {
  const { t, i18n } = useTranslation("admin");
  const navigate = useNavigate();
  const search = useSearch({ from: "/admin/admin/users" });
  const roles = useAdminRoles();
  const users = useAdminUsers({
    ...(search.q !== undefined ? { q: search.q } : {}),
    ...(search.status !== undefined ? { status: search.status } : {}),
    ...(search.role !== undefined ? { role: search.role } : {}),
  });

  // Les pages chargées sont aplaties : le curseur ne sert qu'à aller plus loin,
  // et l'écran affiche la liste continue, non une page isolée.
  const rows = users.data?.pages.flatMap((page) => page.data);

  const formatDate = (value: string | null) =>
    value === null
      ? t("users.never")
      : new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(value));

  const update = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: "/admin/users",
      search: (previous) => {
        const next = { ...previous, ...patch };
        // Une valeur vide est retirée de l'URL : `?status=` n'a pas de sens et
        // rendrait le lien plus difficile à relire.
        return Object.fromEntries(Object.entries(next).filter(([, value]) => Boolean(value)));
      },
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">{t("users.heading")}</h1>

      <Card>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <label htmlFor="admin-search" className="block text-sm font-medium">
              {t("users.search")}
            </label>
            <input
              id="admin-search"
              type="search"
              defaultValue={search.q ?? ""}
              placeholder={t("users.searchPlaceholder")}
              onChange={(event) => {
                update({ q: event.target.value });
              }}
              className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-1 focus:outline-none dark:border-slate-700 dark:bg-slate-900"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="admin-status" className="block text-sm font-medium">
              {t("users.filterStatus")}
            </label>
            <select
              id="admin-status"
              value={search.status ?? ""}
              onChange={(event) => {
                update({ status: event.target.value });
              }}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="">{t("users.any")}</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`status.${status}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="admin-role" className="block text-sm font-medium">
              {t("users.filterRole")}
            </label>
            <select
              id="admin-role"
              value={search.role ?? ""}
              onChange={(event) => {
                update({ role: event.target.value });
              }}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="">{t("users.any")}</option>
              {(roles.data ?? []).map((role) => (
                <option key={role.slug} value={role.slug}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {users.isPending && <Skeleton className="h-4 w-2/3" label={t("users.loading")} />}
      {users.isError && <ErrorState error={users.error} onRetry={() => void users.refetch()} />}

      {rows !== undefined &&
        (rows.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("users.empty")}</p>
          </Card>
        ) : (
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t("users.columns.user")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("users.columns.status")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("users.columns.roles")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("users.columns.lastLogin")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {rows.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-3">
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: user.id }}
                        className="text-brand-600 font-medium hover:underline"
                      >
                        {user.display_name}
                      </Link>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.status} />
                      {/* Le champ est obligatoire dans le schéma : il vaut
                          `null` sur un compte ordinaire, jamais `undefined`.
                          C'est ce qui empêche ce marqueur de s'afficher sur
                          toute la liste (HANDOFF §2). */}
                      {user.deletion_scheduled_for !== null && (
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                          {t("users.deletionPending")}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {user.roles.join(", ")}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {formatDate(user.last_login_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}

      {rows !== undefined && rows.length > 0 && (
        <LoadMore
          shown={rows.length}
          hasMore={users.hasNextPage}
          isLoading={users.isFetchingNextPage}
          onLoadMore={() => void users.fetchNextPage()}
        />
      )}
    </div>
  );
}
